/**
 * langDetect.js, site-wide UI language bootstrap.
 *
 * Precedence on FIRST visit only (once resolved, choice is sticky):
 *   1. explicit URL prefix (/hu, /pl, /de)
 *   2. localStorage (keystone_app_lang)  ← user's saved choice, wins on every subsequent visit
 *   3. navigator.language / navigator.languages
 *   4. IP-based hint from /api/geo/language-hint (HU/PL/DE-speaking countries)
 *   5. 'en' fallback
 *
 * Storage key is the same one DomTranslator already reads (`keystone_app_lang`)
 * so app + marketing site share a single language state.
 */
export const SUPPORTED_LANGS = ['en', 'hu', 'pl', 'de'];
export const LANG_LABELS_FULL = { en: 'English', hu: 'Magyar', pl: 'Polski', de: 'Deutsch' };
export const LANG_FLAGS = { en: '🇬🇧', hu: '🇭🇺', pl: '🇵🇱', de: '🇩🇪' };

const LS_LANG = 'keystone_app_lang';
const LS_AUTO_DONE = 'keystone_lang_auto_done';
const API = process.env.REACT_APP_BACKEND_URL;

export const getStoredLang = () => {
  try {
    const v = localStorage.getItem(LS_LANG);
    return SUPPORTED_LANGS.includes(v) ? v : null;
  } catch { return null; }
};

export const setStoredLang = (lang, { reload = true } = {}) => {
  if (!SUPPORTED_LANGS.includes(lang)) return;
  try {
    localStorage.setItem(LS_LANG, lang);
    localStorage.setItem(LS_AUTO_DONE, '1');
    document.documentElement.setAttribute('lang', lang);
  } catch { /* quota / private mode */ }
  if (reload) window.location.reload();
};

/**
 * Set of route prefixes for which we have localized static HTML +
 * pre-rendered SEO metadata. Switching language on these routes
 * changes the URL (e.g. /site/features → /hu/site/features) so Google
 * can index each language as a separate document. Anywhere else we
 * fall back to localStorage + reload so DomTranslator handles the
 * runtime swap.
 */
export const LOCALIZED_ROUTE_PREFIXES = [
  '/site/features', '/site/how-it-works', '/site/about', '/site/pricing',
  '/site/contact', '/site/security', '/guides', '/',
];

const pathHasLocalizedSupport = (bare) => {
  // Match bare === '/' or bare starts with any explicit prefix (except '/'
  // which we handle specially). Legacy landing pages (e.g. /excel-vs-keystone)
  // and guides (/guides/:slug) are also localized-supported.
  if (bare === '/' || bare === '') return true;
  if (bare.startsWith('/guides')) return true;
  if (bare.startsWith('/site/')) {
    return ['/site/features', '/site/how-it-works', '/site/about', '/site/pricing',
            '/site/contact', '/site/security'].some((p) => bare === p || bare.startsWith(p + '/'));
  }
  // Config-driven SEO landing pages sit at the top level. Treat any
  // single-segment route as localizable (the static generator emits
  // per-lang variants for all of them).
  const segments = bare.split('/').filter(Boolean);
  return segments.length === 1;
};

/**
 * Switch the site to a new language by navigating the URL, so search
 * engines see a distinct localized URL rather than one shared URL with
 * client-side translation. Behaviour:
 *   • Strip any existing /hu, /pl, /de prefix from the current path
 *   • For English, use the bare path (no prefix)
 *   • For hu/pl/de, prepend the prefix IF the route has localized
 *     static HTML. Otherwise fall back to plain localStorage (the SPA
 *     still translates the DOM via DomTranslator).
 * Preserves query string + hash so deep links keep working.
 */
export const switchLanguage = (lang) => {
  if (!SUPPORTED_LANGS.includes(lang)) return;
  try {
    localStorage.setItem(LS_LANG, lang);
    localStorage.setItem(LS_AUTO_DONE, '1');
  } catch { /* noop */ }

  const { pathname, search, hash } = window.location;
  // Strip existing /hu, /pl, /de prefix from the pathname
  const bare = pathname.replace(/^\/(hu|pl|de)(?=\/|$)/, '') || '/';

  let target;
  if (lang === 'en') {
    target = bare;
  } else if (pathHasLocalizedSupport(bare)) {
    // Ensure exactly one leading slash after the prefix
    target = `/${lang}${bare === '/' ? '' : bare}`;
  } else {
    // Non-localized route (admin, /app, etc.), keep the URL as-is and let
    // DomTranslator handle the DOM swap on reload.
    target = bare;
  }

  const dest = target + (search || '') + (hash || '');
  if (dest === (pathname + search + hash)) {
    window.location.reload();     // same URL, reload so DomTranslator picks up
  } else {
    window.location.assign(dest); // navigate (full load = correct static HTML)
  }
};

/** Extract language from a URL prefix, e.g. `/de/site/pricing` → 'de'. */
export const langFromUrl = () => {
  const m = window.location.pathname.match(/^\/(hu|pl|de)(\/|$)/);
  return m ? m[1] : null;
};

/** Read navigator languages, return first supported non-English. */
const detectBrowserLang = () => {
  const list = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language || ''];
  for (const raw of list) {
    const base = (raw || '').toLowerCase().split('-')[0];
    if (SUPPORTED_LANGS.includes(base)) return base;
  }
  return null;
};

const detectIpLang = async () => {
  try {
    const r = await fetch(`${API}/api/geo/language-hint`, { credentials: 'omit' });
    if (!r.ok) return null;
    const d = await r.json();
    return SUPPORTED_LANGS.includes(d.suggested_lang) ? d.suggested_lang : null;
  } catch { return null; }
};

/**
 * Bootstrap language on app start.
 * - Never overwrites a user's explicit choice (localStorage).
 * - Runs auto-detect once per browser, then flags it done.
 * - If the URL is already prefixed (/hu, /pl, /de), that wins and gets persisted.
 */
export async function autoInitLang() {
  // 1. URL prefix has final say (deep-linked / shared URL)
  const urlLang = langFromUrl();
  if (urlLang) {
    if (urlLang !== getStoredLang()) {
      try { localStorage.setItem(LS_LANG, urlLang); localStorage.setItem(LS_AUTO_DONE, '1'); } catch { /* noop */ }
    }
    document.documentElement.setAttribute('lang', urlLang);
    return urlLang;
  }

  // 2. Sticky user choice
  const stored = getStoredLang();
  if (stored) {
    document.documentElement.setAttribute('lang', stored);
    return stored;
  }

  // 3. First-ever visit: try browser, then IP
  let picked = detectBrowserLang();
  if (!picked || picked === 'en') {
    const ipLang = await detectIpLang();
    if (ipLang && ipLang !== 'en') picked = ipLang;
  }
  picked = picked || 'en';
  try {
    localStorage.setItem(LS_LANG, picked);
    localStorage.setItem(LS_AUTO_DONE, '1');
  } catch { /* noop */ }
  document.documentElement.setAttribute('lang', picked);
  return picked;
}

/** Has auto-detect already run at least once? */
export const autoDetectDone = () => {
  try { return localStorage.getItem(LS_AUTO_DONE) === '1'; } catch { return false; }
};
